import { Component, OnInit, inject } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import { AvailabilityCode } from "@app/models/rest/availability-code";
import { AvailabilityCodeStore } from "@app/services/store/availability-code-store";
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogContent,
  MatDialogActions
} from "@angular/material/dialog";
import { ShiftStore } from "@app/services/store/roster/shift-store";
import moment from "moment";
import { Shift } from "@app/models/rest/roster/shift";
import { SubDivision } from "@app/models/rest/roster/sub-division";
import { Roster } from "@app/models/rest/roster/roster";
import {
  MatSliderChange,
  MatSlider,
  MatSliderThumb
} from "@angular/material/slider";
import { MatSelectChange, MatSelect } from "@angular/material/select";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { TimesheetCode } from "@app/models/rest/timesheet-code";
import { firstValueFrom } from "rxjs";
import { TimesheetCodeStore } from "@app/services/store/timesheet-code-store";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { CdkDrag, CdkDragHandle } from "@angular/cdk/drag-drop";
import { MatIconButton, MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { NgxUiLoaderModule } from "ngx-ui-loader";
import { MatError, MatFormField, MatLabel } from "@angular/material/form-field";
import { MatOption } from "@angular/material/core";
import { MatInput } from "@angular/material/input";
import { TimesheetCodeChooserComponent } from "../../../../shared/timesheet-code-chooser/timesheet-code-chooser.component";
import { TranslatePipe } from "@ngx-translate/core";
import { DialogHeaderComponent } from "../../../../shared/dialog-header/dialog-header.component";

@Component({
  selector: "app-shift-editor",
  templateUrl: "./shift-editor.component.html",
  styleUrls: ["./shift-editor.component.css"],
  imports: [
    CdkScrollable,
    MatDialogContent,
    NgxUiLoaderModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatError,
    MatSelect,
    MatOption,
    MatInput,
    MatSlider,
    MatSliderThumb,
    TimesheetCodeChooserComponent,
    MatDialogActions,
    MatButton,
    TranslatePipe,
    DialogHeaderComponent
  ]
})
export class ShiftEditorComponent implements OnInit {
  shiftStore = inject(ShiftStore);
  availabilityCodeStore = inject(AvailabilityCodeStore);
  dialogRef = inject<MatDialogRef<ShiftEditorComponent>>(MatDialogRef);
  fb = inject(UntypedFormBuilder);
  config = inject(ClientConfigService);
  timesheetCodeStore = inject(TimesheetCodeStore);
  serverErrorHandler = inject(ServiceErrorHandlerService);
  data = inject<{
    shiftId: number;
    offsetDay: number;
    subDivision: SubDivision;
    roster: Roster;
  }>(MAT_DIALOG_DATA);

  public shift: Shift;
  public shiftEditorForm: UntypedFormGroup;
  public availabilityCodes: AvailabilityCode[];
  public loaded: boolean = false;
  public cyclicalPriorityReservationPercent: number = 100;
  public normalPriorityReservationPercent: number = 100;
  public highPriorityReservationPercent: number = 100;
  public emergencyPriorityReservationPercent: number = 100;
  public days: { value: number; display: string }[] = [];
  public divisions: SubDivision[] = [];
  sliderConfig = {
    cyclical: {
      value: "cyclicalPriorityReservationPercent",
      higherThan: [],
      lowerThan: ["normal", "high", "emergency"]
    },
    normal: {
      value: "normalPriorityReservationPercent",
      higherThan: ["cyclical"],
      lowerThan: ["high", "emergency"]
    },
    high: {
      value: "highPriorityReservationPercent",
      higherThan: ["cyclical", "normal"],
      lowerThan: ["emergency"]
    },
    emergency: {
      value: "emergencyPriorityReservationPercent",
      higherThan: ["cyclical", "normal", "high"],
      lowerThan: []
    }
  };

  public timesheetCodes: TimesheetCode[];

  public selectedTravelCodeLoaded: boolean;
  public selectedTravelCode: TimesheetCode;
  public selectedWorkCodeLoaded: boolean;
  public selectedWorkCode: TimesheetCode;
  public selectedOverCodeLoaded: boolean;
  public selectedOverCode: TimesheetCode;
  public timesheetCodeMode: boolean;

  public isAssignType: boolean;
  public isBlockType: boolean;
  private selectedCode: AvailabilityCode;

  constructor() {
    this.shiftEditorForm = this.fb.group({
      startTime: null,
      endTime: null,
      offsetDay: null,
      selectedDay: null,
      selectedDivision: [null, Validators.required],
      overDuration: null,
      overTime: null,
      travelTimesheetCode: null,
      workingTimesheetCode: null,
      overrideTimesheetCode: null,
      cyclicalReservationPercent: 100,
      normalReservationPercent: 100,
      highReservationPercent: 100,
      emergencyReservationPercent: 100
    });
    this.timesheetCodeMode = this.config?.config?.enableTimesheetCodes;

    // Initialize days array based on roster length
    if (this.data.roster) {
      this.initDaysArray();
    }
  }

  initDaysArray() {
    if (!this.data.roster) return;

    const rosterLength = this.data.roster.length || 7; // Default to 7 if not set
    this.days = [];

    // Create days array from 0 to roster length - 1
    for (let i = 0; i < rosterLength; i++) {
      this.days.push({
        value: i,
        display: `Day ${i + 1}`
      });
    }
  }

  async loadDivisions() {
    if (!this.data.roster) return [];

    try {
      const response = await firstValueFrom(
        this.data.roster.getRelatedCollection<any>("subDivisions")
      );
      this.divisions = response.resources;
      return this.divisions;
    } catch (error) {
      console.error("Error loading divisions:", error);
      this.serverErrorHandler.handleError(error);
      return [];
    }
  }

  ngOnInit() {
    // Initialize days array
    this.initDaysArray();

    // Load divisions and availability codes in parallel
    Promise.all([
      this.loadDivisions(),
      this.availabilityCodeStore.loadAll(
        { params: { projection: "detail" } },
        true
      ),
      // Only fetch timesheet codes when the feature is enabled (the pickers are
      // hidden otherwise). Load them up front so they are populated when first
      // adding a shift, not only when editing an existing one.
      this.timesheetCodeMode
        ? this.timesheetCodeStore.loadAll()
        : Promise.resolve(null)
    ])
      .then(([_, availabilityCodes, timesheetCodes]) => {
        this.availabilityCodes = availabilityCodes.resources;
        if (timesheetCodes) {
          this.timesheetCodes = timesheetCodes.resources;
        }

        if (this.data.shiftId) {
          // Editing existing shift
          this.shiftStore.loadSingle(this.data.shiftId).then((value) => {
            this.shift = value;
            console.log(value);
            this.resetForm();
            if (this.timesheetCodeMode) {
              this.loadTimesheetCode(
                "travelTimesheetCode",
                "selectedTravelCode"
              );
              this.loadTimesheetCode(
                "workingTimesheetCode",
                "selectedWorkCode"
              );
              this.loadTimesheetCode(
                "overrideTimesheetCode",
                "selectedOverCode"
              );
            }
            this.loaded = true;
          });
        } else {
          // Creating new shift
          this.shiftEditorForm.patchValue({
            cyclicalReservationPercent: 100,
            normalReservationPercent: 100,
            highReservationPercent: 100,
            emergencyReservationPercent: 100,
            selectedDay: this.data.offsetDay,
            selectedDivision: this.data.subDivision
          });
          this.loaded = true;
        }
      })
      .catch((error) => {
        console.error("Error initializing shift editor:", error);
        this.serverErrorHandler.handleError(error);
      });
  }

  async loadTimesheetCode(key: string, stateVariable: string) {
    try {
      const timesheetCode = await firstValueFrom(
        this.shift.getRelation<TimesheetCode>(key)
      );
      const matchedCode = this.timesheetCodes.find(
        (code) => code.id === timesheetCode.id
      );

      if (matchedCode) {
        this[stateVariable] = matchedCode;
        this.shift[key] = matchedCode;
      }
    } catch (error) {
      console.error("Failed to load timesheet code:", error);
    } finally {
      this[stateVariable + "Loaded"] = true;
    }
  }

  async loadSubDivision() {
    try {
      if (!this.shift) return;

      const subDivision = await firstValueFrom(
        this.shift.getRelation<SubDivision>("subDivision")
      );

      // Find the matching division in our divisions list
      const matchedDivision = this.divisions.find(
        (div) => div.id === subDivision.id
      );

      if (matchedDivision) {
        this.shiftEditorForm.patchValue({
          selectedDivision: matchedDivision
        });
      }
    } catch (error) {
      console.error("Failed to load subdivision:", error);
    }
  }

  resetForm() {
    let startTime = moment();
    startTime.hour(this.shift.startOffsetHour);
    startTime.minute(this.shift.startOffsetMinute);
    let startTimeValue = startTime.format("HH:mm");
    let endTime = startTime.add(this.shift.duration, "minutes");
    let endTimeValue = endTime.format("HH:mm");

    //reset to prevent double add
    startTime = moment();
    startTime.hour(this.shift.startOffsetHour);
    startTime.minute(this.shift.startOffsetMinute);

    let overTime = startTime.add(this.shift.overDuration, "minutes");
    let overTimeValue = overTime.format("HH:mm") ?? null;

    console.log("Overtime calculation ", overTime, overTimeValue);

    this.shiftEditorForm.patchValue({
      startTime: startTimeValue,
      endTime: endTimeValue,
      overTime: overTimeValue,
      selectedDay: this.shift.offsetDay,
      cyclicalReservationPercent: this.shift.cyclicalReservationPercent ?? 100,
      normalReservationPercent: this.shift.normalReservationPercent ?? 100,
      highReservationPercent: this.shift.highReservationPercent ?? 100,
      emergencyReservationPercent: this.shift.emergencyReservationPercent ?? 100
    });

    // Load subdivision separately since it's a relation
    this.loadSubDivision();
    this.cyclicalPriorityReservationPercent =
      this.shift.cyclicalReservationPercent ?? 100;
    this.normalPriorityReservationPercent =
      this.shift.normalReservationPercent ?? 100;
    this.highPriorityReservationPercent =
      this.shift.highReservationPercent ?? 100;
    this.emergencyPriorityReservationPercent =
      this.shift.emergencyReservationPercent ?? 100;

    this.shiftEditorForm.markAsPristine();
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onDeleteClick() {
    console.log("Deleting ", this.shift);
    this.shiftStore.delete(this.shift).subscribe(() => {
      this.dialogRef.close();
    });
  }

  async save() {
    if (!this.shift) {
      this.shift = new Shift();
      this.shift.offsetDay = this.data.offsetDay;
      this.shift.subDivision = this.data.subDivision;
    }

    // Store the selected subdivision for later use
    const selectedDivision = this.shiftEditorForm.value.selectedDivision;

    this.prepareShiftData();
    try {
      this.shift = await firstValueFrom(this.upsertShift());

      // Update timesheet codes
      await this.updateTimesheetCode(
        "travelTimesheetCode",
        this.selectedTravelCode
      );
      await this.updateTimesheetCode(
        "workingTimesheetCode",
        this.selectedWorkCode
      );
      await this.updateTimesheetCode(
        "overrideTimesheetCode",
        this.selectedOverCode
      );

      // Update subdivision if one was selected
      if (selectedDivision) {
        await this.updateSubDivision(selectedDivision);
      }

      this.dialogRef.close(this.shift);
    } catch (error) {
      this.serverErrorHandler.handleError(error);
    }
  }

  async updateSubDivision(division: SubDivision) {
    console.log("Updating subdivision to", division);
    try {
      // Using bindRelation to update the subdivision
      await firstValueFrom(this.shift.bindRelation("subDivision", division));
    } catch (error) {
      console.error("Error updating subdivision:", error);
      throw error;
    }
  }

  prepareShiftData() {
    let startTime = this.shiftEditorForm.value.startTime;
    let endTime = this.shiftEditorForm.value.endTime;
    let overTime = this.shiftEditorForm.value.overTime;
    let selectedDay = this.shiftEditorForm.value.selectedDay;
    let selectedDivision = this.shiftEditorForm.value.selectedDivision;

    let processedStartTime = moment(startTime, "HH:mm");
    let processedEndTime = moment(endTime, "HH:mm");
    let processedOverTime = moment(overTime, "HH:mm");

    let startMoment = moment();
    let endMoment = moment();
    let overMoment = moment();

    startMoment
      .hour(processedStartTime.hour())
      .minute(processedStartTime.minute());
    endMoment.hour(processedEndTime.hour()).minute(processedEndTime.minute());
    overMoment
      .hour(processedOverTime.hour())
      .minute(processedOverTime.minute());

    if (endMoment.isBefore(startMoment)) {
      endMoment.add(1, "day");
    }
    if (overMoment.isBefore(startMoment)) {
      overMoment.add(1, "day");
    }

    let duration = endMoment.diff(startMoment, "minutes");

    let overDuration = overMoment.diff(startMoment, "minutes");
    this.shift.cyclicalReservationPercent =
      this.cyclicalPriorityReservationPercent;
    this.shift.normalReservationPercent = this.normalPriorityReservationPercent;
    this.shift.highReservationPercent = this.highPriorityReservationPercent;
    this.shift.emergencyReservationPercent =
      this.emergencyPriorityReservationPercent;
    this.shift.startOffsetHour = processedStartTime.hour();
    this.shift.startOffsetMinute = processedStartTime.minute();
    this.shift.duration = duration;
    this.shift.overDuration = overDuration;

    // Update day and division if provided in form
    if (selectedDay !== null && selectedDay !== undefined) {
      this.shift.offsetDay = selectedDay;
    }

    if (selectedDivision) {
      this.shift.subDivision = selectedDivision;
    }
  }

  upsertShift() {
    return this.shift.id
      ? this.shiftStore.update(this.shift)
      : this.shiftStore.create(this.shift);
  }

  async updateTimesheetCode(relationKey: string, selectedCode: TimesheetCode) {
    console.log("Updating timesheet code", relationKey, selectedCode);
    const currentCode = this.shift[relationKey];
    if (selectedCode) {
      if (currentCode !== selectedCode) {
        await firstValueFrom(
          this.shift.bindRelation(relationKey, selectedCode)
        );
      }
    } else if (currentCode) {
      await firstValueFrom(this.shift.deleteRelation(relationKey, currentCode));
    }
  }

  formatLabel(value: number) {
    return value + "%";
  }

  onSliderChange(sliderName: string, value: number) {
    const slider = this.sliderConfig[sliderName];
    this[slider.value] = value;
    for (const higher of slider.higherThan) {
      if (this[this.sliderConfig[higher].value] > value) {
        this[this.sliderConfig[higher].value] = value;
      }
    }
    for (const lower of slider.lowerThan) {
      if (this[this.sliderConfig[lower].value] < value) {
        this[this.sliderConfig[lower].value] = value;
      }
    }
    this.shiftEditorForm.markAsDirty();
  }

  startTimeChanged($event: Event) {
    console.log($event);
  }

  selectedCodeChange($event: MatSelectChange) {
    console.log("code change", $event.value);
    this.selectedCode = $event.value;
    switch ($event.value.availabilityType) {
      case "ASSIGN":
        this.isAssignType = true;
        this.isBlockType = false;
        break;
      case "BLOCK":
        this.isAssignType = false;
        this.isBlockType = true;
        break;
    }
  }

  handleWorkCodeChange($event: TimesheetCode) {
    this.shiftEditorForm.markAsDirty();
    this.selectedWorkCode = $event;
  }

  handleTravelCodeChange($event: TimesheetCode) {
    console.log("Travel code changed", $event);
    this.shiftEditorForm.markAsDirty();
    this.selectedTravelCode = $event;
  }

  handleOverCodeChange($event: TimesheetCode) {
    this.shiftEditorForm.markAsDirty();
    this.selectedOverCode = $event;
  }

  overTimeChanged($event: Event) {
    this.shiftEditorForm.markAsDirty();
    console.log("Overtime changed");
  }

  handleDayChange($event: any) {
    this.shiftEditorForm.markAsDirty();
    console.log("Day changed to:", $event.value);
  }

  handleDivisionChange($event: any) {
    this.shiftEditorForm.markAsDirty();
    console.log("Division changed to:", $event.value);
  }

  // Match divisions by id so a pre-selected division (e.g. from a double-click
  // on a division row) shows as selected even though it is a different object
  // instance than the ones loaded into the dropdown.
  compareDivisions(a: SubDivision, b: SubDivision): boolean {
    return a?.id === b?.id;
  }
}
